import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GetAllParagraphs,
  CreateParagraph,
  EditParagraph,
  DeleteParagraph,
  RegenerateParagraphContent
} from "../../services/paragraphs";

// Initial State
const initialState = {
  paragraphs: [],
  loading: false,
  error: null,
};

// Slice
const paragraphSlice = createSlice({
  name: "paragraphs",
  initialState,
  reducers: {
    setparagraph: (state, { payload }) => {
      state.paragraphs = payload;
    },
    removesingleparagraph: (state, { payload }) => {
      state.paragraphs = state.paragraphs.filter((i) => i.id !== payload); // ✅ Fixed key
    },
  },
  extraReducers: (builder) => {
    builder

      // ───── GET ALL ─────
      .addCase(getAllParagraphsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllParagraphsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.paragraphs = action.payload;
      })
      .addCase(getAllParagraphsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // ───── CREATE ─────
      .addCase(createParagraphAsync.fulfilled, (state, action) => {
        state.paragraphs.push(action.payload);
      })

      // ───── EDIT ─────
      .addCase(editParagraphAsync.fulfilled, (state, action) => {
        const index = state.paragraphs.findIndex(
          (t) => t.id === action.payload.id
        );
        if (index !== -1) {
          state.paragraphs[index] = {
            ...state.paragraphs[index],
            ...action.payload.updated,
          };
        }
      })

      // ───── DELETE ─────
      .addCase(deleteParagraphAsync.fulfilled, (state, action) => {
        state.paragraphs = state.paragraphs.filter(
          (t) => t.id !== action.payload
        );
      })

      .addCase(regenerateParagraphContentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(regenerateParagraphContentAsync.fulfilled, (state) => {
        // The reply is `{ response }`, not a paragraph; the editor applies it
        // to its own text field. It used to be pushed into `paragraphs`,
        // which nothing reads, as a paragraph-shaped object without an id.
        state.loading = false;
        state.error = null;
      })
      .addCase(regenerateParagraphContentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
  },
});

// ──────────────────────────────
// Async Thunks
// ──────────────────────────────

export const getAllParagraphsAsync = createAsyncThunk(
  "paragraphs/getAllParagraphsAsync",
  async () => {
    const response = await GetAllParagraphs();
    return response;
  }
);

export const createParagraphAsync = createAsyncThunk(
  "paragraphs/createParagraphAsync",
  async (payload) => {
    const response = await CreateParagraph(payload);
    return response;
  }
);

export const editParagraphAsync = createAsyncThunk(
  "paragraphs/editParagraphAsync",
  async ({ id, payload }) => {
    const response = await EditParagraph(payload, id);
    return { id, updated: response };
  }
);

export const deleteParagraphAsync = createAsyncThunk(
  "paragraphs/deleteParagraphAsync",
  async (id) => {
    await DeleteParagraph(id);
    return id;
  }
);

export const regenerateParagraphContentAsync = createAsyncThunk(
    "paragraphs/regenerateParagraphContentAsync",
    async (payload) => {
      const response = await RegenerateParagraphContent(payload);
      return response;
    }
  );

// ──────────────────────────────
// Exports
// ──────────────────────────────

export const { setparagraph, removesingleparagraph } =
  paragraphSlice.actions;

export default paragraphSlice.reducer;
